/**
 * RenderCoordinator — Single authority for canvas redraws.
 *
 * RULE: No component anywhere in the codebase is allowed to call `layer.batchDraw()` directly.
 * Instead, components call `markDirty()` to request a redraw.
 * The coordinator coalesces all dirty flags and calls `batchDraw()` exactly ONCE per frame
 * during playback, or on the next RAF when paused.
 *
 * Phase A: Dirty-flag system replacing 37+ scattered batchDraw calls.
 * Phase B: Integrates VideoRegistry frame capture + FrameBudgetController.
 *          During playback, Phase 3 (frame capture) and Phase 4 (effects) run
 *          inside the coordinator tick, bounded by the adaptive frame budget.
 *
 * Phases 1 (Clock), 2 (MediaSync), 5 (FxCompute), 6 (Apply) still run in
 * Timeline.tsx and Editor2TimelineShell via vb:playhead-tick for now.
 * Full consolidation is deferred to a future iteration to limit blast radius.
 */

import type Konva from "konva";
import { captureFrames, applyEffects } from "./VideoRegistry";
import { computeFrameBudget, sampleFrameTime } from "./FrameBudgetController";
import { createFrameEffectsBridge, type EffectsConfig } from "./FrameEffectsWorkerBridge";

// ---------------------------------------------------------------------------
// Singleton state
// ---------------------------------------------------------------------------

let _layer: Konva.Layer | null = null;
let _dirty = false;
let _isPlaying = false;
let _rafId: number | null = null;
let _lastTickTs = 0;

// Worker bridge singleton — lazily created on first dispatch
let _effectsBridge: ReturnType<typeof createFrameEffectsBridge> | null = null;

function _getEffectsBridge() {
  if (!_effectsBridge) _effectsBridge = createFrameEffectsBridge();
  return _effectsBridge;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Bind the coordinator to the active Konva layer.
 * Call once when the editor mounts, and again with `null` on unmount.
 */
export function setRenderLayer(layer: Konva.Layer | null): void {
  _layer = layer;
  if (!layer) {
    if (_rafId !== null) {
      cancelAnimationFrame(_rafId);
      _rafId = null;
    }
    // Terminate worker bridge when editor unmounts
    _effectsBridge?.terminate();
    _effectsBridge = null;
  }
}

/**
 * Notify the coordinator that playback state changed.
 * During playback the coordinator draws every frame.
 * When paused it only draws when dirty.
 */
export function setRenderPlaying(playing: boolean): void {
  _isPlaying = playing;
  if (playing && _rafId === null && _layer) {
    _lastTickTs = performance.now();
    _startLoop();
  }
}

/**
 * Request a canvas redraw. This is the **only** way components should trigger
 * a Konva layer repaint. The actual `batchDraw()` is coalesced into at most
 * one call per animation frame.
 */
export function markDirty(): void {
  _dirty = true;
  // If not playing, schedule a one-shot RAF to service the dirty flag.
  if (!_isPlaying && _rafId === null && _layer) {
    _rafId = requestAnimationFrame(_oneShotTick);
  }
}

/**
 * Force an immediate synchronous redraw. Use sparingly — only for cases where
 * the next visual frame MUST reflect a change before any async work continues
 * (e.g. element deletion confirmation, crop commit).
 */
export function flushDraw(): void {
  if (_layer) {
    _layer.batchDraw();
    _dirty = false;
  }
}

/**
 * Returns true if playback is active (for external consumers that need to
 * know whether the coordinator is driving redraws).
 */
export function isRenderPlaying(): boolean {
  return _isPlaying;
}

// ---------------------------------------------------------------------------
// V2 playback gate
// ---------------------------------------------------------------------------

let _v2PlaybackActive = false;

/**
 * Signal that PlaybackEngineV2 is driving the frame. V1 satellite loops
 * (scrub-sync RAFs, WebCodecs preview ticks, etc.) should bail when this
 * returns true so they do not compete with V2 for media elements.
 *
 * Phase 1 wiring: set `true` when `usePlaybackEngineV2` activates and
 * `false` on dispose / flag-off. No-op while V2 is disabled.
 */
export function setV2PlaybackActive(active: boolean): void {
  _v2PlaybackActive = active;
}

export function isV2PlaybackActive(): boolean {
  return _v2PlaybackActive;
}

// ---------------------------------------------------------------------------
// Internal loop
// ---------------------------------------------------------------------------

function _startLoop(): void {
  if (_rafId !== null) return;
  _rafId = requestAnimationFrame(_playbackTick);
}

/**
 * Master playback tick — runs every frame during playback.
 *
 * Execution order per frame:
 *   Phase 1 (Clock):        ← Timeline.tsx via vb:playhead-tick (external, unchanged)
 *   Phase 2 (MediaSync):    ← Editor2TimelineShell sync frame (external, unchanged)
 *   Phase 3 (FrameCapture): ← VideoRegistry.captureFrames() (HERE)
 *   Phase 4 (Effects):      ← VideoRegistry.applyEffects() (HERE)
 *   Phase 5 (FxCompute):    ← Editor2TimelineShell FX packets (external, unchanged)
 *   Phase 6 (Apply):        ← Editor2TimelineShell Konva node updates (external, unchanged)
 *   Phase 7 (Draw):         ← layer.batchDraw() (HERE — the ONLY one)
 */
function _playbackTick(timestamp: number): void {
  _rafId = null;
  if (!_layer) return;

  const frameStart = performance.now();

  // ── Phase 3: Frame Capture (bounded by budget) ──────────────────────
  // Captures new video frames for all active videos that have a dirty flag
  // set by their RVFC callback. Each video's drawImage runs at most once.
  const budget = computeFrameBudget();
  captureFrames(budget);

  // ── Phase 4: Effects (bounded by budget) ────────────────────────────
  // Applies LUT, face blur, gradient fade in-place on each video's frame canvas.
  applyEffects(budget);

  // ── Phase 7: Draw (the ONLY batchDraw in the system) ────────────────
  if (_isPlaying || _dirty) {
    _layer.batchDraw();
    _dirty = false;
  }

  // ── Telemetry ───────────────────────────────────────────────────────
  const frameMs = performance.now() - frameStart;
  sampleFrameTime(frameMs);
  _lastTickTs = timestamp;

  // Schedule next frame
  if (_isPlaying) {
    _rafId = requestAnimationFrame(_playbackTick);
  }
}

/**
 * One-shot tick for paused/idle state.
 * Runs once to service a dirty flag, then stops.
 */
function _oneShotTick(): void {
  _rafId = null;
  if (!_layer) return;
  if (_dirty) {
    _layer.batchDraw();
    _dirty = false;
  }
  // If playing started between schedule and execution, hand off to playback loop.
  if (_isPlaying) {
    _startLoop();
  }
}

// ---------------------------------------------------------------------------
// Tab hibernation (visibility-change pause/resume)
// ---------------------------------------------------------------------------

/**
 * Install a listener that pauses playback when the browser tab is hidden
 * and resumes when it becomes visible again.
 *
 * Returns a cleanup function that removes the listener.
 */
export function installTabHibernation(callbacks: {
  onHide: () => void;
  onShow: () => void;
}): () => void {
  const handler = () => {
    if (document.visibilityState === "hidden") {
      callbacks.onHide();
    } else {
      callbacks.onShow();
    }
  };
  document.addEventListener("visibilitychange", handler);
  return () => document.removeEventListener("visibilitychange", handler);
}

// ---------------------------------------------------------------------------
// Worker dispatch (fire-and-forget, 1-frame latency)
// ---------------------------------------------------------------------------

/**
 * Fire-and-forget: send a frame to the FrameEffectsWorker for processing.
 * Result populates the effect cache asynchronously via `onResult` on the
 * NEXT frame (~16ms latency, imperceptible at 60fps). Returns immediately.
 * No-op when the worker is unavailable; inline processing remains fallback.
 */
export function dispatchFrameToWorker(
  elementId: string,
  source: HTMLVideoElement | HTMLCanvasElement,
  width: number,
  height: number,
  effects: EffectsConfig,
  onResult: (bitmap: ImageBitmap) => void,
): void {
  const bridge = _getEffectsBridge();
  if (!bridge.isAvailable()) return;

  createImageBitmap(source)
    .then((bitmap) =>
      bridge.processFrame(elementId, bitmap, width, height, effects),
    )
    .then((result) => {
      if (result) onResult(result);
    })
    .catch(() => {
      /* Worker unavailable or source detached — inline fallback handles it */
    });
}

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------

/**
 * Terminate the worker bridge and release resources.
 * Safe to call multiple times; the bridge will re-create on next dispatch.
 */
export function cleanupRenderCoordinator(): void {
  _effectsBridge?.terminate();
  _effectsBridge = null;
}
